import React, { useState, useEffect } from 'react';
import { 
  LayoutDashboard, MessageSquare, Calendar, FileText, Briefcase, 
  Star, AlertTriangle, Settings, ChevronRight, Trash2,
  LogOut, RefreshCw, Mail, Phone, MapPin, Check, X, Download
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/logo.png";

const statusColors = {
  new: 'bg-blue-100 text-blue-800',
  pending: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-green-100 text-green-800',
  approved: 'bg-green-100 text-green-800',
  reviewed: 'bg-purple-100 text-purple-800',
  contacted: 'bg-indigo-100 text-indigo-800',
  completed: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-red-100 text-red-800',
  sent: 'bg-green-100 text-green-800',
  resolved: 'bg-emerald-100 text-emerald-800',
};

const jobRoleLabels = {
  care_assistant: 'Care Assistant',
  senior_care_assistant: 'Senior Care Assistant',
  registered_nurse: 'Registered Nurse',
  activities_coordinator: 'Activities Coordinator',
  housekeeping: 'Housekeeping',
  kitchen_staff: 'Kitchen Staff',
  maintenance: 'Maintenance',
  admin: 'Admin',
  manager: 'Manager',
};

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [authHeader, setAuthHeader] = useState('');
  const [stats, setStats] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [visitBookings, setVisitBookings] = useState([]);
  const [brochureRequests, setBrochureRequests] = useState([]);
  const [jobApplications, setJobApplications] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [bedAvailability, setBedAvailability] = useState({ nursing_care: 0, residential_care: 0, dementia_nursing: 0, dementia_residential: 0, mental_health_nursing: 0, bariatric_nursing: 0 });
  const [jobRoles, setJobRoles] = useState({});
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  const handleLogin = async (e) => {
    if (e) e.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    
    console.log('Attempting login with:', credentials.username);
    console.log('API URL:', `${API_BASE}/admin/login`);
    
    try {
      // Use custom login endpoint with JSON body to avoid browser popup
      const response = await axios.post(`${API_BASE}/admin/login`, {
        username: credentials.username,
        password: credentials.password
      });
      
      console.log('Login response:', response.data);
      
      // Store credentials for subsequent requests
      const auth = btoa(`${credentials.username}:${credentials.password}`);
      const header = `Basic ${auth}`;
      setAuthHeader(header);
      setIsAuthenticated(true);
      toast.success('Logged in successfully');
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error response:', error.response?.data);
      const errorMsg = error.response?.data?.detail || error.message || 'Login failed';
      setLoginError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setAuthHeader('');
    setCredentials({ username: '', password: '' });
  };

  const fetchData = async () => {
    if (!authHeader) return;
    setLoading(true);
    try {
      const [statsRes, enquiriesRes, bookingsRes, brochuresRes, applicationsRes, reviewsRes, complaintsRes, bedRes, jobsRes] = await Promise.all([
        axios.get(`${API}/stats`, { headers: { Authorization: authHeader } }),
        axios.get(`${API}/enquiries`, { headers: { Authorization: authHeader } }),
        axios.get(`${API}/visit-bookings`, { headers: { Authorization: authHeader } }),
        axios.get(`${API}/brochure-requests`, { headers: { Authorization: authHeader } }),
        axios.get(`${API}/job-applications`, { headers: { Authorization: authHeader } }),
        axios.get(`${API}/reviews`, { headers: { Authorization: authHeader } }),
        axios.get(`${API}/complaints`, { headers: { Authorization: authHeader } }),
        axios.get(`${API}/bed-availability`, { headers: { Authorization: authHeader } }),
        axios.get(`${API}/job-roles`, { headers: { Authorization: authHeader } }),
      ]);
      setStats(statsRes.data);
      setEnquiries(enquiriesRes.data);
      setVisitBookings(bookingsRes.data);
      setBrochureRequests(brochuresRes.data);
      setJobApplications(applicationsRes.data);
      setReviews(reviewsRes.data);
      setComplaints(complaintsRes.data);
      setBedAvailability(bedRes.data);
      setJobRoles(jobsRes.data.roles || {});
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) fetchData();
  }, [isAuthenticated]);

  const updateStatus = async (type, id, newStatus) => {
    const endpoints = { enquiry: 'enquiries', booking: 'visit-bookings', brochure: 'brochure-requests', application: 'job-applications', review: 'reviews', complaint: 'complaints' };
    try {
      await axios.put(`${API}/${endpoints[type]}/${id}/status`, { status: newStatus }, { headers: { Authorization: authHeader } });
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const deleteItem = async (type, id) => {
    const endpoints = { enquiry: 'enquiries', booking: 'visit-bookings', brochure: 'brochure-requests', application: 'job-applications', review: 'reviews', complaint: 'complaints' };
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await axios.delete(`${API}/${endpoints[type]}/${id}`, { headers: { Authorization: authHeader } });
      toast.success('Item deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const downloadCV = async (application) => {
    try {
      const response = await axios.get(
        `${API}/job-applications/${application.id}/cv`,
        { headers: { Authorization: authHeader }, responseType: 'blob' }
      );
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // Try to pull filename out of Content-Disposition, else fallback
      const cd = response.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^";]+)"?/i);
      const filename = match ? match[1] : (application.cv_filename || 'cv.pdf');
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch (error) {
      const msg = error?.response?.status === 404
        ? 'No CV attached to this application'
        : 'Failed to download CV';
      toast.error(msg);
    }
  };

  const updateBedAvailability = async () => {
    try {
      await axios.put(`${API}/bed-availability`, bedAvailability, { headers: { Authorization: authHeader } });
      toast.success('Bed availability updated');
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const toggleJobRole = async (role, active) => {
    try {
      await axios.put(`${API}/job-roles`, { role, active }, { headers: { Authorization: authHeader } });
      setJobRoles({ ...jobRoles, [role]: active });
      toast.success(`${jobRoleLabels[role]} ${active ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <img src={LOGO_URL} alt="Abney Low" className="h-36" />
              <div className="text-left">
                <div className="text-xl font-bold text-[#1e3a5f]">Abney Low</div>
                <div className="text-sm text-slate-600">Nursing Home</div>
              </div>
            </div>
            <CardTitle className="text-xl text-[#1e3a5f]">Admin Dashboard</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label>Username</Label>
                <Input value={credentials.username} onChange={(e) => setCredentials({ ...credentials, username: e.target.value })} className="mt-2 rounded-xl" placeholder="admin" data-testid="admin-username" />
              </div>
              <div>
                <Label>Password</Label>
                <Input type="password" value={credentials.password} onChange={(e) => setCredentials({ ...credentials, password: e.target.value })} className="mt-2 rounded-xl" data-testid="admin-password" />
              </div>
              {loginError && (
                <div className="text-red-500 text-sm text-center p-2 bg-red-50 rounded">{loginError}</div>
              )}
              <Button 
                type="submit" 
                onClick={handleLogin}
                disabled={loginLoading}
                className="w-full bg-[#1e3a5f] text-white hover:bg-[#162c4a] rounded-full" 
                data-testid="admin-login-btn"
              >
                {loginLoading ? 'Logging in...' : 'Login'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-dashboard">
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Abney Low" className="h-24" />
              <div>
                <h1 className="text-lg font-bold text-[#1e3a5f]">Abney Low</h1>
                <p className="text-xs text-slate-500">Admin Dashboard</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={fetchData} disabled={loading}><RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Refresh</Button>
              <Button variant="ghost" onClick={handleLogout}><LogOut className="w-4 h-4 mr-2" />Logout</Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white rounded-xl p-1 mb-8 flex-wrap h-auto gap-1">
            <TabsTrigger value="overview" className="rounded-lg data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white"><LayoutDashboard className="w-4 h-4 mr-2" />Overview</TabsTrigger>
            <TabsTrigger value="enquiries" className="rounded-lg data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white"><MessageSquare className="w-4 h-4 mr-2" />Enquiries</TabsTrigger>
            <TabsTrigger value="applications" className="rounded-lg data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white"><Briefcase className="w-4 h-4 mr-2" />Applications</TabsTrigger>
            <TabsTrigger value="reviews" className="rounded-lg data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white"><Star className="w-4 h-4 mr-2" />Reviews</TabsTrigger>
            <TabsTrigger value="availability" className="rounded-lg data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white"><Settings className="w-4 h-4 mr-2" />Vacancies & Beds</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-slate-500">Total Enquiries</p><p className="text-3xl font-bold text-[#1e3a5f]">{stats?.total?.enquiries || 0}</p></div><div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center"><MessageSquare className="w-6 h-6 text-blue-600" /></div></div>{stats?.pending?.new_enquiries > 0 && <Badge className="mt-2 bg-blue-100 text-blue-800">{stats.pending.new_enquiries} new</Badge>}</CardContent></Card>
              <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-slate-500">Visit Bookings</p><p className="text-3xl font-bold text-[#1e3a5f]">{stats?.total?.visit_bookings || 0}</p></div><div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center"><Calendar className="w-6 h-6 text-green-600" /></div></div>{stats?.pending?.pending_visits > 0 && <Badge className="mt-2 bg-yellow-100 text-yellow-800">{stats.pending.pending_visits} pending</Badge>}</CardContent></Card>
              <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-slate-500">Reviews</p><p className="text-3xl font-bold text-[#1e3a5f]">{stats?.total?.reviews || 0}</p></div><div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center"><Star className="w-6 h-6 text-amber-600" /></div></div>{stats?.pending?.pending_reviews > 0 && <Badge className="mt-2 bg-yellow-100 text-yellow-800">{stats.pending.pending_reviews} pending</Badge>}</CardContent></Card>
              <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-slate-500">Complaints</p><p className="text-3xl font-bold text-[#1e3a5f]">{stats?.total?.complaints || 0}</p></div><div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center"><AlertTriangle className="w-6 h-6 text-red-600" /></div></div>{stats?.pending?.new_complaints > 0 && <Badge className="mt-2 bg-red-100 text-red-800">{stats.pending.new_complaints} new</Badge>}</CardContent></Card>
            </div>
          </TabsContent>

          {/* Enquiries Tab - Combined view */}
          <TabsContent value="enquiries">
            <div className="space-y-6">
              {/* Contact Enquiries */}
              <Card>
                <CardHeader className="pb-3"><CardTitle className="text-[#1e3a5f] text-lg flex items-center gap-2"><MessageSquare className="w-5 h-5" />Contact Enquiries</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {enquiries.slice(0, 5).map((e) => (
                      <div key={e.id} className="p-3 bg-slate-50 rounded-lg flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-[#1e3a5f] flex items-center justify-center text-white text-sm">{e.name?.charAt(0)}</div>
                          <div>
                            <p className="font-medium text-sm">{e.name}</p>
                            <p className="text-xs text-slate-500">{e.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={`${statusColors[e.status]} text-xs`}>{e.status}</Badge>
                          <Select value={e.status} onValueChange={(v) => updateStatus('enquiry', e.id, v)}><SelectTrigger className="w-28 h-7 text-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="new">New</SelectItem><SelectItem value="contacted">Contacted</SelectItem><SelectItem value="completed">Completed</SelectItem></SelectContent></Select>
                          <Button variant="ghost" size="sm" onClick={() => deleteItem('enquiry', e.id)} className="text-red-600 h-7 w-7 p-0"><Trash2 className="w-3 h-3" /></Button>
                        </div>
                      </div>
                    ))}
                    {enquiries.length === 0 && <p className="text-center text-slate-500 py-4 text-sm">No enquiries</p>}
                  </div>
                </CardContent>
              </Card>

              {/* Visit Bookings */}
              <Card>
                <CardHeader className="pb-3"><CardTitle className="text-[#1e3a5f] text-lg flex items-center gap-2"><Calendar className="w-5 h-5" />Visit Bookings</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {visitBookings.slice(0, 5).map((b) => (
                      <div key={b.id} className="p-3 bg-slate-50 rounded-lg flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center text-white text-sm">{b.name?.charAt(0)}</div>
                          <div>
                            <p className="font-medium text-sm">{b.name}</p>
                            <p className="text-xs text-slate-500">{b.preferred_date} - {b.preferred_time}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={`${statusColors[b.status]} text-xs`}>{b.status}</Badge>
                          <Select value={b.status} onValueChange={(v) => updateStatus('booking', b.id, v)}><SelectTrigger className="w-28 h-7 text-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="pending">Pending</SelectItem><SelectItem value="confirmed">Confirmed</SelectItem><SelectItem value="completed">Completed</SelectItem></SelectContent></Select>
                          <Button variant="ghost" size="sm" onClick={() => deleteItem('booking', b.id)} className="text-red-600 h-7 w-7 p-0"><Trash2 className="w-3 h-3" /></Button>
                        </div>
                      </div>
                    ))}
                    {visitBookings.length === 0 && <p className="text-center text-slate-500 py-4 text-sm">No bookings</p>}
                  </div>
                </CardContent>
              </Card>

              {/* Brochure Requests */}
              <Card>
                <CardHeader className="pb-3"><CardTitle className="text-[#1e3a5f] text-lg flex items-center gap-2"><FileText className="w-5 h-5" />Brochure Requests</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {brochureRequests.slice(0, 5).map((r) => (
                      <div key={r.id} className="p-3 bg-slate-50 rounded-lg flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center text-white text-sm">{r.name?.charAt(0)}</div>
                          <div>
                            <p className="font-medium text-sm">{r.name}</p>
                            <p className="text-xs text-slate-500">{r.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={`${statusColors[r.status]} text-xs`}>{r.status}</Badge>
                          <Select value={r.status} onValueChange={(v) => updateStatus('brochure', r.id, v)}><SelectTrigger className="w-28 h-7 text-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="pending">Pending</SelectItem><SelectItem value="sent">Sent</SelectItem></SelectContent></Select>
                          <Button variant="ghost" size="sm" onClick={() => deleteItem('brochure', r.id)} className="text-red-600 h-7 w-7 p-0"><Trash2 className="w-3 h-3" /></Button>
                        </div>
                      </div>
                    ))}
                    {brochureRequests.length === 0 && <p className="text-center text-slate-500 py-4 text-sm">No requests</p>}
                  </div>
                </CardContent>
              </Card>

              {/* Complaints */}
              {complaints.length > 0 && (
                <Card className="border-red-200">
                  <CardHeader className="pb-3"><CardTitle className="text-red-600 text-lg flex items-center gap-2"><AlertTriangle className="w-5 h-5" />Complaints</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {complaints.map((c) => (
                        <div key={c.id} className="p-3 bg-red-50 rounded-lg border-l-4 border-red-500">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium text-sm">{c.name} - <span className="text-red-600">{c.complaint_type}</span></p>
                              <p className="text-xs text-slate-600 mt-1">{c.description}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className={`${statusColors[c.status]} text-xs`}>{c.status}</Badge>
                              <Select value={c.status} onValueChange={(v) => updateStatus('complaint', c.id, v)}><SelectTrigger className="w-28 h-7 text-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="new">New</SelectItem><SelectItem value="reviewed">Reviewed</SelectItem><SelectItem value="resolved">Resolved</SelectItem></SelectContent></Select>
                              <Button variant="ghost" size="sm" onClick={() => deleteItem('complaint', c.id)} className="text-red-600 h-7 w-7 p-0"><Trash2 className="w-3 h-3" /></Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Applications Tab */}
          <TabsContent value="applications">
            <Card><CardHeader><CardTitle className="text-[#1e3a5f]">Job Applications</CardTitle></CardHeader><CardContent>
              <div className="space-y-4">
                {jobApplications.map((a) => (
                  <div key={a.id} className="p-4 bg-slate-50 rounded-xl">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-amber-600 flex items-center justify-center text-white font-medium">{a.name?.charAt(0)}</div>
                        <div>
                          <p className="font-medium text-slate-900">{a.name}</p>
                          <div className="text-sm text-slate-500 mt-1">{a.email} • {a.phone}</div>
                          <div className="flex items-center gap-3 mt-2 flex-wrap">
                            <Badge className="bg-[#1e3a5f] text-white">{a.position}</Badge>
                            <span className="text-sm text-slate-600">{a.experience} experience</span>
                            {a.cv_filename ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => downloadCV(a)}
                                className="h-7 px-2 text-xs text-green-700 border-green-300 hover:bg-green-50"
                                data-testid={`download-cv-${a.id}`}
                                title={a.cv_filename}
                              >
                                <Download className="w-3.5 h-3.5 mr-1" />
                                Download CV
                              </Button>
                            ) : (
                              <span className="text-xs text-slate-400 italic">No CV attached</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <Badge className={statusColors[a.status]}>{a.status}</Badge>
                        <p className="text-xs text-slate-500">{formatDate(a.created_at)}</p>
                        <div className="flex gap-2 mt-2">
                          <Select value={a.status} onValueChange={(v) => updateStatus('application', a.id, v)}><SelectTrigger className="w-32 h-8 text-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="new">New</SelectItem><SelectItem value="reviewed">Reviewed</SelectItem><SelectItem value="contacted">Contacted</SelectItem><SelectItem value="rejected">Rejected</SelectItem></SelectContent></Select>
                          <Button variant="ghost" size="sm" onClick={() => deleteItem('application', a.id)} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {jobApplications.length === 0 && <p className="text-center text-slate-500 py-8">No applications yet</p>}
              </div>
            </CardContent></Card>
          </TabsContent>

          {/* Reviews Tab with Sub-tabs */}
          <TabsContent value="reviews">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-[#1e3a5f]">Reviews Management</CardTitle>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="pending" className="w-full">
                  <TabsList className="mb-4 bg-slate-100">
                    <TabsTrigger value="pending" className="data-[state=active]:bg-[#c9a227] data-[state=active]:text-white">
                      Pending Approval ({reviews.filter(r => r.status === 'pending' || r.status === 'new').length})
                    </TabsTrigger>
                    <TabsTrigger value="approved" className="data-[state=active]:bg-green-600 data-[state=active]:text-white">
                      Approved ({reviews.filter(r => r.status === 'approved').length})
                    </TabsTrigger>
                    <TabsTrigger value="rejected" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
                      Rejected ({reviews.filter(r => r.status === 'rejected').length})
                    </TabsTrigger>
                  </TabsList>

                  {/* Pending Reviews */}
                  <TabsContent value="pending">
                    <div className="space-y-3">
                      {reviews.filter(r => r.status === 'pending' || r.status === 'new').map((r) => (
                        <div key={r.id} className="p-4 bg-amber-50 rounded-xl border-l-4 border-amber-400">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 rounded-full bg-amber-600 flex items-center justify-center text-white font-medium">{r.name?.charAt(0)}</div>
                              <div>
                                <p className="font-medium text-slate-900">{r.name}</p>
                                <p className="text-sm text-slate-500">{r.relationship}</p>
                                <div className="flex mt-1">{[...Array(5)].map((_, i) => <Star key={i} className={`w-4 h-4 ${i < r.rating ? 'text-[#c9a227] fill-current' : 'text-slate-300'}`} />)}</div>
                                <p className="text-slate-600 mt-2 text-sm">"{r.review_text}"</p>
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              <p className="text-xs text-slate-500">{formatDate(r.created_at)}</p>
                              <div className="flex gap-2">
                                <Button size="sm" onClick={() => updateStatus('review', r.id, 'approved')} className="bg-green-600 hover:bg-green-700 text-white h-8"><Check className="w-4 h-4 mr-1" />Approve</Button>
                                <Button size="sm" variant="outline" onClick={() => updateStatus('review', r.id, 'rejected')} className="text-red-600 border-red-300 h-8"><X className="w-4 h-4 mr-1" />Reject</Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                      {reviews.filter(r => r.status === 'pending' || r.status === 'new').length === 0 && (
                        <p className="text-center text-slate-500 py-8">No pending reviews</p>
                      )}
                    </div>
                  </TabsContent>

                  {/* Approved Reviews */}
                  <TabsContent value="approved">
                    <div className="space-y-3">
                      {reviews.filter(r => r.status === 'approved').map((r) => (
                        <div key={r.id} className="p-4 bg-green-50 rounded-xl border-l-4 border-green-500">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 rounded-full bg-green-600 flex items-center justify-center text-white font-medium">{r.name?.charAt(0)}</div>
                              <div>
                                <p className="font-medium text-slate-900">{r.name}</p>
                                <p className="text-sm text-slate-500">{r.relationship}</p>
                                <div className="flex mt-1">{[...Array(5)].map((_, i) => <Star key={i} className={`w-4 h-4 ${i < r.rating ? 'text-[#c9a227] fill-current' : 'text-slate-300'}`} />)}</div>
                                <p className="text-slate-600 mt-2 text-sm">"{r.review_text}"</p>
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              <Badge className="bg-green-100 text-green-800">Live on Website</Badge>
                              <p className="text-xs text-slate-500">{formatDate(r.created_at)}</p>
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => updateStatus('review', r.id, 'rejected')} className="text-red-600 border-red-300 h-8"><X className="w-4 h-4 mr-1" />Remove</Button>
                                <Button variant="ghost" size="sm" onClick={() => deleteItem('review', r.id)} className="text-red-600 h-8"><Trash2 className="w-4 h-4" /></Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                      {reviews.filter(r => r.status === 'approved').length === 0 && (
                        <p className="text-center text-slate-500 py-8">No approved reviews</p>
                      )}
                    </div>
                  </TabsContent>

                  {/* Rejected Reviews */}
                  <TabsContent value="rejected">
                    <div className="space-y-3">
                      {reviews.filter(r => r.status === 'rejected').map((r) => (
                        <div key={r.id} className="p-4 bg-red-50 rounded-xl border-l-4 border-red-400">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 rounded-full bg-red-400 flex items-center justify-center text-white font-medium">{r.name?.charAt(0)}</div>
                              <div>
                                <p className="font-medium text-slate-900">{r.name}</p>
                                <p className="text-sm text-slate-500">{r.relationship}</p>
                                <div className="flex mt-1">{[...Array(5)].map((_, i) => <Star key={i} className={`w-4 h-4 ${i < r.rating ? 'text-[#c9a227] fill-current' : 'text-slate-300'}`} />)}</div>
                                <p className="text-slate-600 mt-2 text-sm">"{r.review_text}"</p>
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              <p className="text-xs text-slate-500">{formatDate(r.created_at)}</p>
                              <div className="flex gap-2">
                                <Button size="sm" onClick={() => updateStatus('review', r.id, 'approved')} className="bg-green-600 hover:bg-green-700 text-white h-8"><Check className="w-4 h-4 mr-1" />Approve</Button>
                                <Button variant="ghost" size="sm" onClick={() => deleteItem('review', r.id)} className="text-red-600 h-8"><Trash2 className="w-4 h-4" /></Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                      {reviews.filter(r => r.status === 'rejected').length === 0 && (
                        <p className="text-center text-slate-500 py-8">No rejected reviews</p>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Vacancies & Beds Tab */}
          <TabsContent value="availability">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Bed Availability */}
              <Card>
                <CardHeader><CardTitle className="text-[#1e3a5f]">Bed Availability</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Nursing Care</Label>
                      <Input type="number" min="0" value={bedAvailability.nursing_care || 0} onChange={(e) => setBedAvailability({ ...bedAvailability, nursing_care: parseInt(e.target.value) || 0 })} className="mt-2 rounded-xl" />
                    </div>
                    <div>
                      <Label>Residential Care</Label>
                      <Input type="number" min="0" value={bedAvailability.residential_care || 0} onChange={(e) => setBedAvailability({ ...bedAvailability, residential_care: parseInt(e.target.value) || 0 })} className="mt-2 rounded-xl" />
                    </div>
                    <div>
                      <Label>Dementia Nursing</Label>
                      <Input type="number" min="0" value={bedAvailability.dementia_nursing || 0} onChange={(e) => setBedAvailability({ ...bedAvailability, dementia_nursing: parseInt(e.target.value) || 0 })} className="mt-2 rounded-xl" />
                    </div>
                    <div>
                      <Label>Dementia Residential</Label>
                      <Input type="number" min="0" value={bedAvailability.dementia_residential || 0} onChange={(e) => setBedAvailability({ ...bedAvailability, dementia_residential: parseInt(e.target.value) || 0 })} className="mt-2 rounded-xl" />
                    </div>
                    <div>
                      <Label>Mental Health Nursing</Label>
                      <Input type="number" min="0" value={bedAvailability.mental_health_nursing || 0} onChange={(e) => setBedAvailability({ ...bedAvailability, mental_health_nursing: parseInt(e.target.value) || 0 })} className="mt-2 rounded-xl" />
                    </div>
                    <div>
                      <Label>Bariatric Nursing</Label>
                      <Input type="number" min="0" value={bedAvailability.bariatric_nursing || 0} onChange={(e) => setBedAvailability({ ...bedAvailability, bariatric_nursing: parseInt(e.target.value) || 0 })} className="mt-2 rounded-xl" />
                    </div>
                  </div>
                  <Button onClick={updateBedAvailability} className="w-full bg-[#c9a227] text-[#1e3a5f] hover:bg-[#d4b03a] rounded-full">Save Bed Availability</Button>
                </CardContent>
              </Card>

              {/* Job Roles */}
              <Card>
                <CardHeader><CardTitle className="text-[#1e3a5f]">Job Vacancies</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  {Object.entries(jobRoleLabels).map(([key, label]) => (
                    <div key={key} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                      <span className="font-medium text-slate-700">{label}</span>
                      <Switch checked={jobRoles[key] || false} onCheckedChange={(checked) => toggleJobRole(key, checked)} />
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminDashboard;
